import type { Metadata } from "next";
import type { Listing, Vendor } from "@/types";

export const siteMeta = {
  siteName: "Pacific North North",
  baseUrl: "https://pacificnorthnorth.com",
  defaultTitle: "Pacific North North — Local Alaska & Pacific Northwest Adventures",
  defaultDescription:
    "Discover locally-crafted adventures in Alaska and the Pacific Northwest. Book directly with trusted pilots, guides, and outfitters.",
};

export const defaultOG = {
  image: "https://pacificnorthnorth-images-noprod.s3.us-east-1.amazonaws.com/assets/og-pacificnorthnorth.png",
  width: 1200,
  height: 630,
  type: "website" as const,
};

/**
 * Build a full page title with site name
 */
export function buildTitle(pageTitle?: string): string {
  if (!pageTitle) return siteMeta.defaultTitle;
  return `${pageTitle} | ${siteMeta.siteName}`;
}

/**
 * Build canonical URL for a page
 */
export function buildCanonical(path: string): string {
  // If path is empty, return just the baseUrl
  if (!path || path === "") {
    return siteMeta.baseUrl;
  }
  // Remove leading slash if present to avoid double slashes
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  return `${siteMeta.baseUrl}/${cleanPath}`;
}

/**
 * Generate OpenGraph metadata for a page
 */
export function generateOGMetadata(
  title: string,
  description: string,
  url: string,
  image?: string
): Metadata["openGraph"] {
  return {
    title,
    description,
    url,
    siteName: siteMeta.siteName,
    images: [
      {
        url: image || defaultOG.image,
        width: defaultOG.width,
        height: defaultOG.height,
        alt: title,
      },
    ],
    locale: "en_US",
    type: defaultOG.type,
  };
}

/**
 * Generate Twitter card metadata
 */
export function generateTwitterMetadata(
  title: string,
  description: string,
  image?: string
): Metadata["twitter"] {
  return {
    card: "summary_large_image",
    title,
    description,
    images: [image || defaultOG.image],
  };
}

/**
 * Generate full metadata object for a page
 */
export function generatePageMetadata({
  title,
  description,
  path,
  image,
  noindex = false,
}: {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  noindex?: boolean;
}): Metadata {
  const pageTitle = title ? buildTitle(title) : siteMeta.defaultTitle;
  const pageDescription = description || siteMeta.defaultDescription;
  const canonical = path ? buildCanonical(path) : siteMeta.baseUrl;

  return {
    title: pageTitle,
    description: pageDescription,
    alternates: {
      canonical,
    },
    openGraph: generateOGMetadata(pageTitle, pageDescription, canonical, image),
    twitter: generateTwitterMetadata(pageTitle, pageDescription, image),
    ...(noindex && { robots: { index: false, follow: false } }),
  };
}

/**
 * Truncate description to max length for SEO
 */
export function truncateDescription(description: string, maxLength: number = 160): string {
  if (description.length <= maxLength) return description;
  return description.slice(0, maxLength - 3) + "...";
}

/**
 * Generate structured data (JSON-LD) for a listing using schema.org/TouristTrip
 */
export function generateListingStructuredData(listing: Listing): object {
  const url = buildCanonical(`listings/${listing.slug || listing.id}`);
  const price = listing.base_price_cents / 100;
  
  // Build image array (hero image + up to 2 more if available)
  const images: string[] = [];
  if (listing.hero_image_url) {
    images.push(listing.hero_image_url);
  }
  // Note: If gallery images exist in the future, add them here
  
  // Parse location (e.g., "Anchorage, Alaska" -> city: "Anchorage", state: "Alaska")
  const locationParts = listing.location.split(",").map(s => s.trim());
  const city = locationParts[0] || listing.location;
  const state = locationParts[1] || "Alaska";
  
  // Build vendor organization
  const vendor = listing.created_by_vendor;
  const vendorOrg = vendor ? {
    "@type": "Organization" as const,
    name: vendor.business_name,
    url: vendor.id ? buildCanonical(`vendors/${vendor.id}`) : undefined,
  } : undefined;
  
  // Build rating aggregate if reviews exist
  const ratingAggregate = listing.average_rating && listing.review_count && listing.review_count > 0 ? {
    "@type": "AggregateRating" as const,
    ratingValue: listing.average_rating,
    reviewCount: listing.review_count,
    bestRating: 5,
    worstRating: 1,
  } : undefined;
  
  // Determine availability based on fulfillment type
  let availability = "https://schema.org/InStock";
  if (listing.fulfillment_type === "open_date_voucher") {
    // Check voucher validity dates
    if (listing.voucher_valid_from && listing.voucher_valid_to) {
      const now = new Date();
      const validFrom = new Date(listing.voucher_valid_from);
      const validTo = new Date(listing.voucher_valid_to);
      if (now < validFrom || now > validTo) {
        availability = "https://schema.org/PreOrder";
      }
    }
  }
  
  interface TouristTripSchema {
    "@context": string;
    "@type": "TouristTrip";
    name: string;
    description: string;
    url: string;
    image?: string[];
    price: string;
    priceCurrency: string;
    availability: string;
    startLocation: {
      "@type": "Place";
      address: {
        "@type": "PostalAddress";
        addressLocality: string;
        addressRegion: string;
        addressCountry: string;
      };
    };
    provider?: {
      "@type": "Organization";
      name: string;
      url?: string;
    };
    aggregateRating?: {
      "@type": "AggregateRating";
      ratingValue: number;
      reviewCount: number;
      bestRating: number;
      worstRating: number;
    };
    duration?: string;
    validFrom?: string;
    validThrough?: string;
  }

  const structuredData: TouristTripSchema = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name: listing.title,
    description: truncateDescription(listing.description, 500),
    url,
    ...(images.length > 0 && { image: images }),
    price: price.toFixed(2),
    priceCurrency: "USD",
    availability,
    startLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: city,
        addressRegion: state,
        addressCountry: "US",
      },
    },
    ...(vendorOrg && { provider: vendorOrg }),
    ...(ratingAggregate && { aggregateRating: ratingAggregate }),
    ...(listing.fulfillment_type === "multi_day_itinerary" && listing.duration_days && {
      duration: `P${listing.duration_days}D`,
    }),
    ...(listing.fulfillment_type === "open_date_voucher" && listing.voucher_valid_from && {
      validFrom: new Date(listing.voucher_valid_from).toISOString(),
    }),
    ...(listing.fulfillment_type === "open_date_voucher" && listing.voucher_valid_to && {
      validThrough: new Date(listing.voucher_valid_to).toISOString(),
    }),
  };
  
  return structuredData;
}

/**
 * Generate structured data for a vendor using schema.org/LocalBusiness
 */
export function generateVendorStructuredData(vendor: Vendor & { slug?: string }): object {
  const slug = vendor.slug || vendor.id;
  const url = buildCanonical(`vendors/${slug}`);
  
  interface LocalBusinessSchema {
    "@context": string;
    "@type": "LocalBusiness";
    name: string;
    url: string;
    description?: string;
    address?: {
      "@type": "PostalAddress";
      addressRegion: string;
      addressCountry: string;
    };
    telephone?: string;
    image?: string;
    foundingDate?: string;
  }
  
  const structuredData: LocalBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: vendor.business_name,
    url,
    description: `Local Alaskan adventure provider: ${vendor.business_name}`,
    ...(vendor.region && {
      address: {
        "@type": "PostalAddress",
        addressRegion: vendor.region,
        addressCountry: "US",
      },
    }),
    ...(vendor.phone && { telephone: vendor.phone }),
    ...(vendor.created_at && { foundingDate: vendor.created_at }),
  };
  
  return structuredData;
}

/**
 * Generate structured data for homepage using schema.org/WebSite with search action
 */
export function generateHomeStructuredData(): object {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteMeta.siteName,
    url: siteMeta.baseUrl,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${siteMeta.baseUrl}/listings?activity={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
    publisher: {
      "@type": "Organization",
      name: siteMeta.siteName,
      url: siteMeta.baseUrl,
      logo: {
        "@type": "ImageObject",
        url: `${siteMeta.baseUrl}/og-pacificnorthnorth.png`,
      },
    },
  };
}

/**
 * Generate structured data for multi-day package using schema.org/ItemList and TouristTrip
 */
export function generateMultiDayStructuredData(listing: Listing): object {
  const baseTrip = generateListingStructuredData(listing);
  
  if (!listing.itinerary_components || listing.itinerary_components.length === 0) {
    return baseTrip;
  }
  
  // Group components by day
  const componentsByDay: Record<number, typeof listing.itinerary_components> = {};
  for (const component of listing.itinerary_components) {
    if (!componentsByDay[component.day_number]) {
      componentsByDay[component.day_number] = [];
    }
    componentsByDay[component.day_number].push(component);
  }
  
  // Build item list
  const itemList = Object.keys(componentsByDay)
    .sort((a, b) => parseInt(a) - parseInt(b))
    .map(dayNum => {
      const dayComponents = componentsByDay[parseInt(dayNum)];
      return {
        "@type": "TouristTrip",
        name: `Day ${dayNum}`,
        description: dayComponents.map(c => c.title).join(", "),
        itinerary: dayComponents.map(component => ({
          "@type": component.component_type === "activity" ? "TouristAttraction" : "Service",
          name: component.title,
          description: component.description,
        })),
      };
    });
  
  return {
    ...baseTrip,
    "@type": "ItemList",
    numberOfItems: itemList.length,
    itemListElement: itemList,
  };
}

/**
 * Generate structured data for voucher using schema.org/Offer
 */
export function generateVoucherStructuredData(listing: Listing): object {
  const baseTrip = generateListingStructuredData(listing);
  const price = listing.base_price_cents / 100;
  
  interface OfferSchema {
    "@type": "Offer";
    price: string;
    priceCurrency: string;
    availability: string;
    url: string;
    validFrom?: string;
    validThrough?: string;
  }
  
  const offer: OfferSchema = {
    "@type": "Offer",
    price: price.toFixed(2),
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    url: buildCanonical(`listings/${listing.slug || listing.id}`),
    ...(listing.voucher_valid_from && {
      validFrom: new Date(listing.voucher_valid_from).toISOString(),
    }),
    ...(listing.voucher_valid_to && {
      validThrough: new Date(listing.voucher_valid_to).toISOString(),
    }),
  };
  
  return {
    ...baseTrip,
    offers: offer,
  };
}

/**
 * Generate breadcrumbs structured data using schema.org/BreadcrumbList
 */
export function generateBreadcrumbsStructuredData(items: Array<{ name: string; url: string }>): object {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Generate listing breadcrumbs
 */
export function generateListingBreadcrumbs(listing: Listing): object {
  const items = [
    { name: "Home", url: siteMeta.baseUrl },
    { name: "Adventures", url: buildCanonical("listings") },
  ];
  
  // Add location if available
  const locationParts = listing.location.split(",").map(s => s.trim());
  if (locationParts[0]) {
    items.push({
      name: locationParts[0],
      url: buildCanonical(`listings?location=${encodeURIComponent(locationParts[0])}`),
    });
  }
  
  // Add listing name
  items.push({
    name: listing.title,
    url: buildCanonical(`listings/${listing.slug || listing.id}`),
  });
  
  return generateBreadcrumbsStructuredData(items);
}

/**
 * Generate metadata for a listing page
 */
export function generateListingMetadata(listing: Listing): Metadata {
  const title = listing.title;
  const description = truncateDescription(listing.description, 160);
  const image = listing.hero_image_url || defaultOG.image;
  const path = `listings/${listing.slug || listing.id}`;
  
  return generatePageMetadata({
    title,
    description,
    path,
    image,
  });
}

/**
 * Generate metadata for a vendor page
 */
export function generateVendorMetadata(vendor: Vendor & { slug?: string }): Metadata {
  const title = vendor.business_name;
  const description = `Book adventures with ${vendor.business_name} - Local Alaskan guides and outfitters`;
  const slug = vendor.slug || vendor.id;
  const path = `vendors/${slug}`;
  
  return generatePageMetadata({
    title,
    description,
    path,
  });
}

/**
 * Generate metadata for homepage
 */
export function generateHomeMetadata(): Metadata {
  return generatePageMetadata({
    title: "Pacific North North – Keeping Adventure Local",
    description: "Book authentic Alaskan adventures directly with local pilots, guides, and outfitters. Alaska-first, locally controlled.",
    path: "",
    image: defaultOG.image,
  });
}

/**
 * Generate metadata for multi-day package
 */
export function generateMultiDayMetadata(listing: Listing): Metadata {
  return generateListingMetadata(listing);
}

/**
 * Generate metadata for voucher
 */
export function generateVoucherMetadata(listing: Listing): Metadata {
  return generateListingMetadata(listing);
}

